import { FieldState, FormState, ValidationResponse } from "formstate";
import { camelCase } from "lodash";
import { inject, observer } from "mobx-react";
import React from "react";
import { SafeLocalStorageService } from "@fidget/common/services/safeLocalStorageService";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import { L10n, L10nHtml } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { scrollToSection } from "@fidget/common/utils/scrollToSection";
import { validateEmail, validateRequired } from "@fidget/common/utils/validatorsFormState";
import { ICurrentUserStore } from "../../../models/currentUser/CurrentUserStore";
import { BeforeLoginSection } from "../components/beforeLoginSection";
import { LoginInstead } from "../components/loginInstead";
import styles from "./adminSignUpStart.css";
import { SignUpCheckbox } from "./signUpCheckbox";
import { getGtmService } from "../../../services/gtmService";
import { staticConfig } from "@fidget/common/config/static-config";

enum LocalStorageNames {
  reseller = "reseller",
  resellerTier = "resellerTier"
}

type FormFields = {
  email: FieldState<string>;
  agreeToTerms: FieldState<boolean>;
  agreeToMarketing: FieldState<boolean>;
};

type UtmParamName = "utm_medium" | "utm_source" | "utm_campaign" | "ref" | "reftype";
type UtmParams = { [F in UtmParamName]: string | undefined };

interface Props {
  currentUserStore?: ICurrentUserStore;
}

@inject("currentUserStore")
@observer
export class AdminSignUpStart extends React.Component<Props> {
  private readonly safeLocalStorage = SafeLocalStorageService.getInstance();
  private readonly gtmService = getGtmService();

  utmParams: UtmParams;
  language: "pl" | "en";
  fields: FormFields;
  form: FormState<FormFields>;
  styleType: "signup" = "signup";
  plan: string;

  constructor(props: Props) {
    super(props);
    this.fields = {
      email: new FieldState("").disableAutoValidation().validators(validateRequired, validateEmail),
      agreeToTerms: new FieldState(false).validators(this.validateAgreeToTerms),
      agreeToMarketing: new FieldState<boolean>(false)
    };
    this.form = new FormState(this.fields);

    const params = new URL(window.location.href).searchParams;
    this.utmParams = (
      ["utm_medium", "utm_source", "utm_campaign", "ref", "reftype"] as UtmParamName[]
    ).reduce(
      (obj, name: UtmParamName) =>
        Object.assign(obj, { [camelCase(name)]: params.get(name) || undefined }),
      {} as UtmParams
    );

    if (!this.utmParams.ref) {
      this.utmParams.ref = this.safeLocalStorage.getItem(LocalStorageNames.reseller) || undefined;
    }
    if (!this.utmParams.reftype) {
      this.utmParams.reftype =
        this.safeLocalStorage.getItem(LocalStorageNames.resellerTier) || undefined;
    }

    this.saveResellerInfo();

    const navigator = window.navigator as any;
    this.language = (navigator.userLanguage || navigator.language).substr(0, 2);
    this.language = this.language === "pl" ? "pl" : "en";

    this.state = {
      displayMode: "completingAccount"
    };

    const query = new URLSearchParams(window.location.search);
    const planName = query.get("plan");

    this.plan = planName || staticConfig.launchPlan;
  }

  saveResellerInfo = (): void => {
    const reseller = this.utmParams.ref;
    const resellerTier = this.utmParams.reftype;

    if (reseller) {
      this.safeLocalStorage.setItem(LocalStorageNames.reseller, reseller);
    }
    if (resellerTier) {
      this.safeLocalStorage.setItem(LocalStorageNames.resellerTier, resellerTier);
    }
  };

  validateAgreeToTerms = (value: boolean): ValidationResponse =>
    !value ? intl.formatMessage("signup.getStarted.mustAgreeToTerms") : "";

  onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { hasError } = await this.form.validate();
    if (!hasError) {
      const data = Object.assign(
        {
          languageTag: this.language,
          email: this.fields.email.$,
          marketingConsent: this.fields.agreeToMarketing.value
        },
        this.utmParams
      );
      const planName = this.plan === "accelerate" ? staticConfig.newestAccelerate : this.plan;
      const success = await this.props
        .currentUserStore!.signupOrg(data, planName)
        .catch((reason) => {
          // signupOrg throws an object with backend error for field - this way we can update form state
          Object.keys(reason).forEach((key) =>
            this.fields[key as keyof FormFields].setError(reason[key])
          );
        });
      if (success) {
        this.safeLocalStorage.removeItem(LocalStorageNames.reseller);
        this.safeLocalStorage.removeItem(LocalStorageNames.resellerTier);

        scrollToSection(1);
        const event =
          this.plan === staticConfig.newestAccelerate
            ? "accelerate-account-email-success"
            : "launch-account-email-success";
        this.gtmService.pushTag(event);
      }
    }
  };

  render() {
    const { email, agreeToTerms, agreeToMarketing } = this.fields;
    return (
      <>
        <BeforeLoginSection styleType={this.styleType} className={styles.section}>
          <header>
            <h2>
              <L10nHtml id={"signup.getStarted.title"} />
            </h2>
            <div>
              <p>
                <L10nHtml id={"signup.getStarted.subtitle"} />
              </p>
            </div>
          </header>

          <form onSubmit={this.onSubmit} id="free-account-email">
            <AgentInput
              styleType={this.styleType}
              field={email}
              placeholder={intl.formatMessage("signup.getStarted.email.placeholder")}
              autoFocus
            />
            <SignUpCheckbox
              checked={!!agreeToTerms.value}
              onChange={agreeToTerms.onChange}
              hasError={agreeToTerms.hasError}
              error={agreeToTerms.error}
            >
              <L10nHtml id={"signup.getStarted.agreeToTerms"} />
            </SignUpCheckbox>
            <SignUpCheckbox
              checked={!!agreeToMarketing.value}
              onChange={agreeToMarketing.onChange}
              hasError={agreeToMarketing.hasError}
              error={agreeToMarketing.error}
            >
              <L10nHtml id={"signup.getStarted.agreeToMarketing"} />
            </SignUpCheckbox>
            <button type="submit">
              <L10n id={"signup.continue"} />
            </button>
          </form>

          <p>
            <a href="https://closer.app/">
              <L10n id={"signup.getStarted.back"} />
            </a>
          </p>

          <LoginInstead />
        </BeforeLoginSection>

        <BeforeLoginSection styleType={this.styleType} className={styles.section}>
          <header>
            <h2>
              <L10n id={"checkYourEmail"} />
            </h2>
            <div>
              <p>
                <L10nHtml id={"signup.getStarted.emailSent"} values={{ email: email.$ }} />
              </p>
            </div>
          </header>

          <p>
            <a href="https://closer.app/">
              <L10n id={"signup.getStarted.back"} />
            </a>
          </p>
        </BeforeLoginSection>
      </>
    );
  }
}
